/**
 * A typed-in list as chips: I1's `product_keywords` (text), E's Quantity options (whole numbers). Type a value and
 * press Enter (or a comma for text, a space or semicolon for numbers) to add it; each chip has its own remove. What
 * is added, what a number list refuses, and that a value is not added twice is `enterTags` in shared-core. Text
 * still in the box when it loses focus is added too, so nothing typed is silently dropped; an entry a number list
 * refuses stays in the box with the reason beneath.
 */
import { useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import {
  enterTags,
  removeTag,
  tagSeparatorKeys,
  type TagItemKind,
  type TagValue,
} from "@shared-core/domain/object-ui/tag-list";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";

const REFUSED_AS: Record<Exclude<TagItemKind, "text">, string> = {
  integer: "a whole number",
  number: "a number",
};

export function TagListInput({
  id,
  label,
  itemKind = "text",
  value,
  disabled,
  invalid = {},
  onChange,
}: {
  id: string;
  label: string;
  itemKind?: TagItemKind;
  value: readonly TagValue[];
  disabled: boolean;
  /** `aria-invalid` / `aria-describedby` from the field's errors. */
  invalid?: Record<string, unknown>;
  onChange: (values: TagValue[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const refusalId = `${id}-entry-error`;

  const commit = (typed: string, rest = "") => {
    const { values, rejected } = enterTags(value, typed, itemKind);
    if (rejected.length > 0 && itemKind !== "text") {
      setRefusal(
        `${rejected.map((r) => `"${r}"`).join(", ")} ${rejected.length === 1 ? "is" : "are"} not ${REFUSED_AS[itemKind]}.`,
      );
      setDraft([rejected.join(" "), rest].filter(Boolean).join(" "));
    } else {
      setRefusal(null);
      setDraft(rest);
    }
    if (values.length !== value.length) onChange(values);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (tagSeparatorKeys(itemKind).includes(event.key)) {
      event.preventDefault();
      commit(draft);
    }
  };

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label={label}>
          {value.map((tag) => (
            <li
              key={String(tag)}
              className="flex items-center gap-1 rounded-md border bg-muted px-2 py-0.5 text-sm tabular-nums"
            >
              {itemKind === "text" ? tag : Number(tag).toLocaleString("en-GB")}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                disabled={disabled}
                aria-label={`Remove ${tag}`}
                onClick={() => onChange(removeTag(value, tag))}
              >
                <X className="h-3 w-3" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Input
        id={id}
        value={draft}
        disabled={disabled}
        inputMode={itemKind === "text" ? undefined : "decimal"}
        placeholder="Type and press Enter"
        {...invalid}
        {...(refusal
          ? { "aria-invalid": true, "aria-describedby": refusalId }
          : {})}
        onChange={(e) => {
          // A paste with separators adds each complete entry; what follows the last one stays to be typed on.
          const text = e.target.value;
          const cut = Math.max(
            ...tagSeparatorKeys(itemKind)
              .filter((key) => key !== "Enter")
              .map((key) => text.lastIndexOf(key)),
          );
          if (cut === -1) return setDraft(text);
          commit(text.slice(0, cut), text.slice(cut + 1));
        }}
        onKeyDown={onKeyDown}
        onBlur={() => draft.trim() && commit(draft)}
      />
      {refusal && (
        <p id={refusalId} className="text-sm text-red-600">
          {refusal}
        </p>
      )}
    </div>
  );
}
